/**
 * Production-safe logger utility
 * Logs are only output in development mode (__DEV__ = true)
 * In production builds, all logging is silently disabled
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
  prefix?: string;
  showTimestamp?: boolean;
}

export interface ErrorBufferEntry {
  ts: number;
  prefix: string;
  message: string;
  args: unknown[];
}

// Rate-limited in-memory ring buffer for production error events. Replaces
// console.error in release builds so no error text (which may contain
// stack traces or redacted error envelopes) reaches Logcat / iOS console.
// A future crash reporter (Sentry / Bugsnag) can flush this buffer.
const ERROR_BUFFER_MAX = 20;
const ERROR_BUFFER_MIN_INTERVAL_MS = 100;
const errorBuffer: ErrorBufferEntry[] = [];
let lastErrorTs = 0;

export const getErrorBuffer = (): ErrorBufferEntry[] => errorBuffer.slice();
export const clearErrorBuffer = (): void => {
  errorBuffer.length = 0;
};

class Logger {
  private prefix: string;
  private showTimestamp: boolean;

  constructor(options: LoggerOptions = {}) {
    this.prefix = options.prefix || '';
    this.showTimestamp = options.showTimestamp ?? false;
  }

  private formatMessage(level: LogLevel, message: string): string {
    const parts: string[] = [];

    if (this.showTimestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }

    if (this.prefix) {
      parts.push(`[${this.prefix}]`);
    }

    parts.push(message);

    return parts.join(' ');
  }

  debug(message: string, ...args: any[]): void {
    if (__DEV__) {
      console.log(this.formatMessage('debug', message), ...args);
    }
  }

  info(message: string, ...args: any[]): void {
    if (__DEV__) {
      console.info(this.formatMessage('info', message), ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (__DEV__) {
      console.warn(this.formatMessage('warn', message), ...args);
    }
  }

  error(message: string, ...args: any[]): void {
    if (__DEV__) {
      console.error(this.formatMessage('error', message), ...args);
      return;
    }

    // Production: rate-limit and buffer. Do NOT console.error — the
    // transform-remove-console babel plugin strips it anyway, but we also
    // keep a bounded in-memory ring buffer the crash reporter can flush.
    const now = Date.now();
    if (now - lastErrorTs < ERROR_BUFFER_MIN_INTERVAL_MS) {
      return;
    }
    lastErrorTs = now;

    errorBuffer.push({
      ts: now,
      prefix: this.prefix,
      message,
      args,
    });

    if (errorBuffer.length > ERROR_BUFFER_MAX) {
      errorBuffer.splice(0, errorBuffer.length - ERROR_BUFFER_MAX);
    }
  }

  // Shorthand for debug with data object
  data(label: string, data: any): void {
    if (__DEV__) {
      console.log(this.formatMessage('debug', label), JSON.stringify(data, null, 2));
    }
  }
}

// Create named loggers for different modules
export const createLogger = (prefix: string, options?: Omit<LoggerOptions, 'prefix'>): Logger => {
  return new Logger({ ...options, prefix });
};

// Default logger instance
export const logger = new Logger();

// Pre-configured loggers for common modules
export const authLogger = createLogger('Auth');
export const apiLogger = createLogger('API');
export const navLogger = createLogger('Navigation');
export const storageLogger = createLogger('Storage');
export const iapLogger = createLogger('IAP');
export const aiLogger = createLogger('AI');
export const contactLogger = createLogger('Contact');
export const taskLogger = createLogger('Task');
export const profileLogger = createLogger('Profile');
export const subscriptionLogger = createLogger('Subscription');
export const biometricLogger = createLogger('Biometric');
export const vaultLogger = createLogger('Vault');
export const documentLogger = createLogger('Document');
export const onboardingLogger = createLogger('Onboarding');
export const dashboardLogger = createLogger('Dashboard');
export const sessionLogger = createLogger('Session');

export default logger;
